// Click-to-play helpers shared by the site room and the CTF playground: what each hex click does
// for the team to act, the walk paths for far clicks, button order, odds and status text.

import {
  unitById, tagNeed, tagBreakdown, tagWhy, EFFECT_NAMES, EFFECT_TIMING, effectText, applyAction, listLegalActions, cellIdAt, describeAction, unitAt, actorTeam, typeOf, slotLabel, zoneText, TRAITS,
  moveOf, playerOf, hasTrait, unitLabel, TEAM_NAMES, prisonCell, prisoners,
} from '../../../games/capture-the-flag/capture-the-flag';
import type { CtfAction, CtfState, Hex, Team } from '../../../games/capture-the-flag/types';
import type { HexHint } from './CtfBoard';

export const hexKey = (h: Hex) => `${h.q},${h.r}`;

// Every hex the active unit can reach with its remaining Move, with the move/turn actions
// that get it there (shortest first). Units move by facing, so turns are part of the path.
export function reachable(g: CtfState, playerId: string): Map<string, CtfAction[]> {
  // Every hex the active unit can still step onto, with the cheapest path there. The search plays
  // moves and turns through the engine itself, so turn costs (slow turns), zone stops and
  // slippery units come out exactly as in play. Buckets go by Move spent: the first visit of a
  // (hex, facing) state is its cheapest.
  const out = new Map<string, CtfAction[]>();
  const a = g.active;
  if (!a || a.moveLocked) return out;
  const u0 = unitById(g, a.unitId);
  if (!u0.pos) return out;
  type Node = { s: CtfState; path: CtfAction[] };
  const keyOf = (s: CtfState) => {
    const u = unitById(s, a.unitId);
    return `${hexKey(u.pos!)},${u.facing}`;
  };
  const seen = new Set<string>();
  const buckets: Node[][] = [[{ s: g, path: [] }]];
  for (let spent = 0; spent < buckets.length; spent++) {
    for (const n of buckets[spent] ?? []) {
      const k = keyOf(n.s);
      if (seen.has(k)) continue;
      seen.add(k);
      if (!n.s.active || n.s.active.unitId !== a.unitId) continue;
      for (const act of listLegalActions(n.s, playerId)) {
        if (act.type !== 'move' && act.type !== 'turn') continue;
        const next = applyAction(n.s, act);
        if (next === n.s) continue;
        const path = [...n.path, act];
        if (act.type === 'move' && !out.has(hexKey(act.to))) out.set(hexKey(act.to), path);   // even a step that scores
        if (!next.active || next.active.unitId !== a.unitId) continue;
        (buckets[a.movesLeft - next.active.movesLeft] ??= []).push({ s: next, path });
      }
    }
  }
  return out;
}

// Odds label ("4+") for a tag-like action.
export function oddsFor(g: CtfState, a: CtfAction): string | undefined {
  try {
    if (a.type === 'tag' && g.active) return `${tagNeed(g, unitById(g, g.active.unitId), unitById(g, a.targetId))}+`;
    if (a.type === 'longShot' && g.pending?.kind === 'longShot') return `${tagNeed(g, unitById(g, g.pending.unitId), unitById(g, a.targetId))}+`;
  } catch { /* unit gone */ }
  return undefined;
}

export interface BoardPlay {
  hints: Map<string, HexHint>;
  boardAction: Map<string, CtfAction>;   // one click does this
  paths: Map<string, CtfAction[]>;       // one click walks this path
  buttons: CtfAction[];                  // everything else, most useful first
}

// Board hints and buttons for the legal actions of the player to act.
export function boardPlay(g: CtfState, legal: CtfAction[], playerId: string | null): BoardPlay {
  const hints = new Map<string, HexHint>();
  const boardAction = new Map<string, CtfAction>();
  for (const a of legal) {
    let at: Hex | null = null;
    let hint: HexHint | null = null;
    if (a.type === 'activate') { at = unitById(g, a.unitId).pos; hint = { kind: 'activate' }; }
    else if (a.type === 'move') { at = a.to; hint = { kind: 'move' }; }
    else if (a.type === 'tag' || a.type === 'longShot') { at = unitById(g, a.targetId).pos; hint = { kind: 'tag', label: oddsFor(g, a) }; }
    if (at && hint && !boardAction.has(hexKey(at))) { hints.set(hexKey(at), hint); boardAction.set(hexKey(at), a); }
  }
  const paths = playerId && legal.length && !g.pending ? reachable(g, playerId) : new Map<string, CtfAction[]>();
  paths.forEach((path, k) => { if (!hints.has(k) && path.length > 1) hints.set(k, { kind: 'reach' }); });
  // Dropping the flag is one button; the hex is then picked on the board (dropView).
  const firstDrop = legal.find(a => a.type === 'dropFlag');
  const buttons = legal.filter(a => a.type !== 'activate' && a.type !== 'move' && (a.type !== 'dropFlag' || a === firstDrop));
  buttons.sort((x, y) => buttonRank(x) - buttonRank(y));
  return { hints, boardAction, paths, buttons };
}

// 2: pass-type buttons (shown last and quieter), 1: turns, 0: the rest.
export const buttonRank = (a: CtfAction) =>
  a.type === 'endActivation' || a.type === 'skipEffect' ? 2 : a.type === 'turn' ? 1 : 0;

// The last tag attempt of the current activation, for the board's tag line.
export function lastTagOf(g: CtfState): { from: Hex; to: Hex; hit: boolean } | null {
  const e = [...g.log].reverse().find(x => x.kind === 'tag-roll' || x.kind === 'activate');
  if (e?.kind !== 'tag-roll') return null;
  try {
    const at = unitById(g, e.data!.attacker as string), tg = unitById(g, e.data!.target as string);
    return at.pos && tg.pos ? { from: at.pos, to: tg.pos, hit: e.data!.success === true } : null;
  } catch { return null; }
}

// How the game ended, for a status line ("Blue wins: brought the flag home.").
export function resultText(g: CtfState, nameOf: (t: Team) => string): string {
  const r = g.result;
  if (!r) return '';
  if (!r.winner) return `A draw on cells after ${r.rounds} rounds (mercy rule).`;
  if (r.reason === 'resign') return `${nameOf(r.winner)} wins: ${nameOf(r.winner === 'A' ? 'B' : 'A')} resigned.`;
  const how = r.reason === 'flag' ? 'brought the flag home' : r.reason === 'domination' ? `held ${r.cells[r.winner]} cells (domination)` : r.reason === 'prison' ? `put the whole enemy army in prison` : `held more cells after ${r.rounds} rounds (mercy rule)`;
  return `${nameOf(r.winner)} wins: ${how}.`;
}

// What the team to act is deciding, after "who: ".
export function decisionText(g: CtfState): string {
  const p = g.pending;
  if (p?.kind === 'reroll') return `reroll? The roll was ${p.roll}, needing ${p.need}+: a ${p.hit ? 'hit' : 'miss'}`;
  if (p) return EFFECT_NAMES[p.kind];
  if (g.active) {
    const u = unitById(g, g.active.unitId);
    return `${unitName(g, u.id)} · ${g.active.movesLeft} move${g.active.movesLeft === 1 ? '' : 's'} left`;
  }
  return 'choose a unit to activate';
}

// ── Preview: a clicked unit is only selected; the activation happens with the first move or action ──

export interface PlayView {
  shown: CtfState;                        // what to draw: the preview position while a unit is selected
  preview: string | null;                  // the selected unit, if the preview is live
  hints: Map<string, HexHint>;
  buttons: CtfAction[];                   // each one runs through `commitButton`
  click: (h: Hex) => { preview?: string | null; actions?: CtfAction[]; cell?: number; unit?: string };
  commitButton: (a: CtfAction) => CtfAction[];
  // A button press: the actions to send, or (Drop the flag) a new selection to pick a hex with.
  press: (a: CtfAction) => { actions?: CtfAction[]; preview?: string | null };
  note?: string;                          // what to do in a picking mode
}

// The selection that means "pick where to drop the flag" (`DROP_PICK` + the carrier's id while it
// is only selected, not yet activated).
export const DROP_PICK = '@drop:';

// The board for the player to act, given the unit they have selected (null: none). Clicking one
// of their units selects it and shows what it could do; the first move, target or button then
// sends the activation and that action together. Clicks that do nothing report the cell.
export function playView(g: CtfState, legal: CtfAction[], playerId: string | null, previewId: string | null): PlayView {
  if (g.pending?.kind === 'blink' && playerId) return blinkView(g, legal, previewId);
  if (previewId?.startsWith(DROP_PICK) && playerId) {
    const v = dropView(g, legal, playerId, previewId.slice(DROP_PICK.length));
    if (v) return v;
  }
  const view = playViewBase(g, legal, playerId, previewId);
  const press = (a: CtfAction) => (a.type === 'dropFlag'
    ? { preview: DROP_PICK + (view.preview ?? '') }
    : { actions: view.commitButton(a) });
  return { ...view, press };
}

// Drop the flag: the carrier's hex and the hexes next to it light up; click one. `selected`: the
// carrier, when it is only selected (the activation goes with the drop).
function dropView(g: CtfState, legal: CtfAction[], playerId: string, selected: string): PlayView | null {
  type Drop = Extract<CtfAction, { type: 'dropFlag' }>;
  const activation = !g.active && selected ? legal.find(a => a.type === 'activate' && a.unitId === selected) ?? null : null;
  const g2 = activation ? applyAction(g, activation) : g;
  const legal2 = activation ? listLegalActions(g2, playerId) : legal;
  const drops = legal2.filter((a): a is Drop => a.type === 'dropFlag');
  if (!drops.length) return null;
  const pre = activation ? [activation] : [];
  const hints = new Map<string, HexHint>();
  for (const d of drops) hints.set(hexKey(d.to), { kind: 'drop' });
  const others = legal2.filter(a => a.type !== 'activate' && a.type !== 'move' && a.type !== 'dropFlag' && a.type !== 'turn');
  others.sort((x, y) => buttonRank(x) - buttonRank(y));
  return {
    shown: g2, preview: activation ? selected : null, hints, buttons: others,
    click: h => {
      const d = drops.find(x => hexKey(x.to) === hexKey(h));
      return d ? { actions: [...pre, d] } : { preview: activation ? selected : null };
    },
    commitButton: a => [...pre, a],
    press: a => ({ actions: [...pre, a] }),
    note: 'Drop the flag: click the carrier\'s hex or a lit hex next to it (it can lie under any unit; exactly on your HQ hex it scores). Click anywhere else to cancel.',
  };
}

function playViewBase(g: CtfState, legal: CtfAction[], playerId: string | null, previewId: string | null): Omit<PlayView, 'press'> {
  const base = boardPlay(g, legal, playerId);
  const cellOf = (h: Hex) => cellIdAt(g.cells, h);
  const activation = previewId && playerId && !g.active && !g.pending
    ? legal.find(a => a.type === 'activate' && a.unitId === previewId) ?? null : null;
  const g2 = activation ? applyAction(g, activation) : g;
  if (!activation || g2 === g || g2.pending) {
    return {
      shown: g, preview: null, hints: base.hints, buttons: base.buttons,
      click: h => {
        const a = base.boardAction.get(hexKey(h));
        if (a?.type === 'activate') {
          const next = applyAction(g, a);
          return next === g || next.pending ? { actions: [a] } : { preview: a.unitId };
        }
        if (a) return { actions: [a] };
        const path = base.paths.get(hexKey(h));
        return path?.length ? { actions: path } : { cell: cellOf(h), unit: unitAt(g, h)?.id };
      },
      commitButton: a => [a],
    };
  }
  const pv = boardPlay(g2, listLegalActions(g2, playerId!), playerId);
  const hints = new Map(pv.hints);
  base.hints.forEach((hint, k) => { if (hint.kind === 'activate' && !hints.has(k)) hints.set(k, hint); });
  return {
    shown: g2, preview: previewId, hints, buttons: pv.buttons,
    click: h => {
      const k = hexKey(h);
      const a = pv.boardAction.get(k), b = base.boardAction.get(k);
      if (b?.type === 'activate') return { preview: b.unitId === previewId ? null : b.unitId };
      if (a) return { actions: [activation, a] };
      const path = pv.paths.get(k);
      return path?.length ? { actions: [activation, ...path] } : { preview: null, cell: cellOf(h), unit: unitAt(g, h)?.id };
    },
    commitButton: a => [activation, a],
  };
}

// What a cell is, for the info card shown when a cell is clicked.
export function cellInfo(g: CtfState, id: number): { title: string; owner: Team | null; effect: string | null; timing: string | null; prison: string | null } {
  const c = g.cells[id];
  const fx = g.effects[id];
  const jail = g.config.prisons ? (['A', 'B'] as const).find(t => prisonCell(g, t) === id) ?? null : null;
  const kind = c.role === 'hq' ? 'HQ' : c.role === 'teleporter' ? 'teleporter' : jail ? `${TEAM_NAMES[jail]}'s prison` : null;
  const held = jail ? prisoners(g, jail) : [];
  return {
    prison: !jail ? null
      : held.length ? `In prison: ${held.map(u => unitLabel(g, u)).join(', ')}. A ${TEAM_NAMES[jail]} unit here can free them all.`
      : 'Empty.',
    title: `Cell ${id}${kind ? ` · ${kind}` : ''}`,
    owner: g.owners[id],
    effect: fx ? effectText(g.config)[fx] : null,
    timing: fx ? EFFECT_TIMING[fx] : null,
  };
}

// Button text for an action in the shown position (the preview position while a unit is selected).
// Every action ends the activation, so with a unit only selected the button names both steps.
export function actionLabel(shown: CtfState, a: CtfAction, preview: string | null): string {
  if (a.type === 'dropFlag') return 'Drop the flag…';
  const odds = oddsFor(shown, a);
  if (a.type === 'endActivation') return preview ? `Activate ${unitName(shown, preview)} and pass` : describeAction(shown, a);
  return `${describeAction(shown, a)}${odds ? ` (needs ${odds})` : ''}`;
}

// One entry per unit type for the units legend, from the game's own squads (both teams' types when
// they differ): its labels, Move, zone and traits.
export function unitLegend(g: CtfState): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const t of ['A', 'B'] as Team[]) {
    const squad = g.config.squads[t];
    squad.forEach(u => {
      const key = JSON.stringify(u);
      if (seen.has(key)) return;
      seen.add(key);
      const labels = squad.map((x, j) => (JSON.stringify(x) === key ? slotLabel(squad, j) : '')).filter(Boolean).join('/');
      const bits = [`Move ${u.move}`, `zone: ${zoneText(u)}`, ...u.traits.map(id => TRAITS[id].name.toLowerCase())];
      parts.push(`${labels} ${u.name.toLowerCase()} (${bits.join(', ')})`);
    });
  }
  return parts.join(' · ');
}

// Blink on the board: click one of your units, then the dot where it would land in a cell you hold.
function blinkView(g: CtfState, legal: CtfAction[], previewId: string | null): PlayView {
  type Blink = Extract<CtfAction, { type: 'blink' }>;
  const blinks = legal.filter((a): a is Blink => a.type === 'blink');
  const units = Array.from(new Set(blinks.map(b => b.unitId)));
  const sel = previewId && units.includes(previewId) ? previewId : null;
  const hints = new Map<string, HexHint>();
  const landing = new Map<string, Blink>();
  for (const id of units) {
    const p = unitById(g, id).pos;
    if (p) hints.set(hexKey(p), { kind: id === sel ? 'select' : 'activate' });
  }
  if (sel) {
    for (const b of blinks.filter(x => x.unitId === sel)) {
      const to = unitById(applyAction(g, b), sel).pos;
      if (to && !landing.has(hexKey(to))) { landing.set(hexKey(to), b); hints.set(hexKey(to), { kind: 'move' }); }
    }
  }
  return {
    shown: g, preview: sel, hints, buttons: legal.filter(a => a.type !== 'blink'),
    press: a => ({ actions: [a] }),
    click: h => {
      const k = hexKey(h);
      const u = units.find(id => { const p = unitById(g, id).pos; return p && hexKey(p) === k; });
      if (u) return { preview: u === sel ? null : u };
      const b = landing.get(k);
      return b ? { actions: [b] } : { cell: cellIdAt(g.cells, h), unit: unitAt(g, h)?.id };
    },
    commitButton: a => [a],
  };
}

// A unit's name as players see it on the board: "Red L", "Blue 2".
export const unitName = (g: CtfState, id: string) => { const u = unitById(g, id); return `${TEAM_NAMES[u.team]} ${unitLabel(g, u)}`; };

// What a unit is and what it can do, for the unit card.
export interface UnitCardInfo {
  name: string;                            // its type's name ("Peasant")
  team: Team;
  move: number;
  moveNote: string;                        // why Move differs from its type's, if it does
  zone: [number, number][];                // its type's zone (offsets, facing direction 0)
  stops: 'until' | 'always' | 'never';     // when its zone stops enemies
  traits: { name: string; text: string }[];
  status: string;
}
export function unitInfo(g: CtfState, id: string): UnitCardInfo {
  const u = unitById(g, id);
  const t = typeOf(g, u);
  const move = moveOf(g, u);
  const moveNote = move === t.move ? '' : u.carrying ? `${t.move} when not carrying the flag` : `${t.move} normally`;
  const stops = hasTrait(g, u, 'noGuardZone') ? 'never' : hasTrait(g, u, 'sentinel') ? 'always' : 'until';
  const status = !u.pos ? 'Off the board, coming back at its teleporter.'
    : [u.carrying ? 'Carrying the flag.' : '', g.active?.unitId === u.id ? 'Activating now.' : u.guarding ? 'On guard until the round ends.' : u.activated ? 'Already activated this round.' : 'Still to activate this round.']
      .filter(Boolean).join(' ');
  return {
    name: t.name, team: u.team, move, moveNote, zone: t.zone, stops,
    traits: t.traits.map(x => ({ name: TRAITS[x].name, text: TRAITS[x].text })), status,
  };
}

// Where a unit could move if it activated now (for looking at any unit, enemies included), as hints.
export function peekHints(g: CtfState, id: string): Map<string, HexHint> {
  const out = new Map<string, HexHint>();
  if (g.phase !== 'playing') return out;
  const u = unitById(g, id);
  if (!u.pos || g.active?.unitId === id) return out;
  const s: CtfState = {
    ...g, turn: u.team, pending: null, queue: [],
    units: g.units.map(x => (x.id === id ? { ...x, activated: false } : x)),
    active: { unitId: id, movesLeft: moveOf(g, u), moved: false, moveLocked: false },
  };
  reachable(s, playerOf(s, u.team).id).forEach((_, k) => out.set(k, { kind: 'peek' }));
  return out;
}

// The most recent tag roll, for the roll panel.
export interface RollInfo { attacker: string; target: string; total: number; need: number; hit: boolean; why: string; longShot: boolean }
export function lastRoll(g: CtfState): RollInfo | null {
  const e = [...g.log].reverse().find(x => x.kind === 'tag-roll');
  if (!e?.data) return null;
  const d = e.data;
  const name = (id: string) => { try { return unitName(g, id); } catch { return id; } };
  return { attacker: name(String(d.attacker)), target: name(String(d.target)), total: Number(d.total), need: Number(d.need), hit: d.success === true, why: String(d.why ?? ''), longShot: d.source === 'longShot' };
}

// The roll each tag on offer needs, and why (from the position shown).
export function tagOffers(shown: CtfState, actions: CtfAction[]): { action: CtfAction; target: string; need: number; why: string }[] {
  const out: { action: CtfAction; target: string; need: number; why: string }[] = [];
  for (const a of actions) {
    const attackerId = a.type === 'tag' ? shown.active?.unitId : a.type === 'longShot' && shown.pending?.kind === 'longShot' ? shown.pending.unitId : null;
    if (!attackerId || (a.type !== 'tag' && a.type !== 'longShot')) continue;
    try {
      const b = tagBreakdown(shown, unitById(shown, attackerId), unitById(shown, a.targetId));
      out.push({ action: a, target: unitName(shown, a.targetId), need: b.need, why: tagWhy(b) });
    } catch { /* unit gone */ }
  }
  return out;
}

